import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Card, Collapse, FormControl, Grid, MenuItem, Paper, Select, Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import SubActivityService from "./Locality/Service/subActivityService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import ActivityService from "./Locality/Service/activityService";
import SuperActivityService from "./Locality/Service/superActivityService";
import StudentService from "./Locality/Service/studentService";
import { formatDate } from "../../utils/dateFormat";
import FilterPanel, { FilterSection } from "../../components/ui/FilterPanel";
import FilterListIcon from "@material-ui/icons/FilterList";
import SchoolIcon from "@material-ui/icons/School";
import PersonIcon from "@material-ui/icons/Person";
import VpnKeyIcon from "@material-ui/icons/VpnKey";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
};

const resolveSchoolId = (userDetails) => {
  const id = userDetails?.schooleId?._id || userDetails?.schooleId
    || userDetails?.schoolId?._id || userDetails?.schoolId;
  return id ? String(id) : "";
};

const STATUS_KEYS = ["P", "WA", "WI", "M"];

const hasDisplayableStatus = (record) => {
  if (!Array.isArray(record) || record.length === 0) return false;
  const history = record[0]?.statusHistory;
  if (!Array.isArray(history)) return false;
  return history.some(
    (item) => STATUS_KEYS.includes(item?.key) && item?.changedAt
  );
};

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    padding: theme.spacing(2.5),
    background: "#f8fafc",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  filtersCard: {
    borderRadius: 22,
    padding: theme.spacing(2, 2.5),
    marginBottom: theme.spacing(2.5),
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
  },
  filterLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "#64748b",
    marginBottom: theme.spacing(0.75),
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  filterSelect: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#f8fafc",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#e2e8f0",
    },
  },
  searchButton: {
    borderRadius: 12,
    padding: theme.spacing(1.1, 2.5),
    textTransform: "none",
    fontWeight: 700,
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#ffffff",
    boxShadow: "0 10px 22px rgba(37, 99, 235, 0.24)",
    height: 40,
    "&:hover": {
      background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    },
  },
  contentCard: {
    borderRadius: 24,
    padding: theme.spacing(3),
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      borderRadius: 18,
    },
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  title: {
    fontWeight: 800,
    fontSize: "1.85rem",
    color: "#0f172a",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.4rem",
    },
  },
  subtitle: {
    color: "#64748b",
    maxWidth: 620,
    lineHeight: 1.6,
    fontSize: "0.95rem",
  },
  helperText: {
    color: "#64748b",
    fontSize: "0.85rem",
    marginBottom: theme.spacing(1.5),
  },
  tabsScroller: {
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  tabsWrap: {
    display: "inline-flex",
    gap: theme.spacing(1.25),
    minWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(1),
    },
  },
  tabButton: {
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontWeight: 700,
    fontSize: "0.9rem",
    borderRadius: 14,
    padding: theme.spacing(1.15, 2),
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#60a5fa",
      backgroundColor: "#eff6ff",
      color: "#1d4ed8",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 1.5),
      fontSize: "0.82rem",
      borderRadius: 12,
    },
  },
  tabButtonActive: {
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    color: "#1d4ed8",
    borderColor: "#60a5fa",
    boxShadow: "0 8px 18px rgba(59, 130, 246, 0.18)",
  },
  tableWrapper: {
    overflowX: "auto",
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
  },
  groupToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  toolbarButton: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.8rem",
    border: "1px solid #cbd5e1",
    color: "#334155",
    padding: theme.spacing(0.5, 1.5),
    "&:hover": {
      borderColor: "#60a5fa",
      backgroundColor: "#eff6ff",
      color: "#1d4ed8",
    },
  },
  groupContainer: {
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    overflow: "hidden",
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.6, 2),
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    cursor: "pointer",
    transition: "background 0.2s ease",
    "&:hover": {
      background: "#eff6ff",
    },
  },
  groupHeaderActive: {
    background: "#eff6ff",
  },
  groupHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  groupChevron: {
    color: "#1d4ed8",
    fontSize: "1.4rem",
    flexShrink: 0,
  },
  groupTitle: {
    fontWeight: 800,
    fontSize: "1rem",
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  groupCount: {
    color: "#64748b",
    fontWeight: 600,
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  table: {
    width: "100%",
    minWidth: 760,
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  headCell: {
    background: "#eff6ff",
    color: "#1e3a8a",
    fontWeight: 800,
    fontSize: "0.9rem",
    padding: theme.spacing(1.75, 1.25),
    textAlign: "center",
    borderBottom: "1px solid #dbeafe",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  firstHeadCell: {
    textAlign: "left",
    minWidth: 220,
  },
  secondHeadCell: {
    textAlign: "left",
    minWidth: 220,
  },
  row: {
    "&:nth-child(even) td": {
      backgroundColor: "#fcfdff",
    },
    "&:hover td": {
      backgroundColor: "#f8fbff",
    },
  },
  activityCell: {
    padding: theme.spacing(1.4, 1.25),
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 700,
    color: "#0f172a",
    backgroundColor: "#f8fafc",
    minWidth: 220,
  },
  subActivityCell: {
    padding: theme.spacing(1.4, 1.25),
    borderBottom: "1px solid #e5e7eb",
    color: "#334155",
    minWidth: 220,
  },
  statusCell: {
    padding: theme.spacing(1.2, 1),
    borderBottom: "1px solid #e5e7eb",
    textAlign: "center",
    fontWeight: 700,
    minWidth: 120,
    fontSize: "0.88rem",
  },
  inactiveStatus: {
    color: "#94a3b8",
  },
  loadingBox: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#2563eb",
    fontWeight: 700,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#64748b",
    fontWeight: 600,
  },
  promptState: {
    textAlign: "center",
    padding: theme.spacing(5, 2),
    color: "#64748b",
    fontWeight: 600,
    borderRadius: 18,
    border: "1px dashed #cbd5e1",
    background: "#f8fafc",
  },
}));

const keyOptions = [
  { label: "Presented (P)", value: "P" },
  { label: "Works with Assistance (WA)", value: "WA" },
  { label: "Works Independently (WI)", value: "WI" },
  { label: "Mastered (M)", value: "M" },
];

export default function ActivityTrackerTable() {
  const classes = useStyles();
  const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
  const schoolId = resolveSchoolId(userDetails);
  const teacherSectionId = normalizeId(userDetails.sectionId);

  const [activityList, setActivityList] = useState([]);
  const [subActivityList, setSubActivityList] = useState([]);
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sectionList, setSectionList] = useState([]);
  const [addSuperActivityList, setAddSuperActivityList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addClassList, setAddClassList] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const handleResetFilters = () => {
    handleClassChange("");
    setSelectedKey("");
  };
  const [activeTab, setActiveTab] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const colorMap = {
    P: "#5C9882",
    WI: "#E6B163",
    WA: "#A75C9C",
    M: "#1a1a1a",
  };

  useEffect(() => {
    getAddClassList();
    return () => {
      setStudentList([]);
      setAddSuperActivityList([]);
      setActivityList([]);
      setSubActivityList([]);
    };
  }, []);

  const onSubmit = useCallback((superActivityId) => {
    if (!studentId || !superActivityId) {
      setActivityList([]);
      return;
    }

    setIsLoading(true);
    const keys = {
      schooleId: schoolId,
      isFuturePlanning: false,
      isInitLoad: true,
      superActivityId,
      studentId,
      classId,
      sectionId: sectionId || teacherSectionId || undefined,
      startDate: "",
      endDate: "",
      status: true,
      key: selectedKey || undefined,
    };

    ActivityService.findActivityReports(keys)
      .then((result) => {
        setActivityList(Array.isArray(result) ? result : []);
        setExpandedGroups({});
      })
      .catch((err) => {
        console.error(err);
        setActivityList([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [studentId, selectedKey, classId, sectionId, schoolId, teacherSectionId]);

  // Auto-load when student / key / area tab changes (no Search button).
  useEffect(() => {
    if (!studentId || !activeTab) {
      if (!studentId) setActivityList([]);
      return;
    }
    onSubmit(activeTab);
  }, [studentId, selectedKey, activeTab, onSubmit]);

  const getSubActivityMasterList = async (selectedClassId) => {
    const schoolId = resolveSchoolId(userDetails);
    if (!schoolId) {
      setSubActivityList([]);
      return;
    }

    try {
      const res = await SubActivityService.getAllSubActivity(schoolId);
      const filtered = (res || []).filter((item) => {
        const itemClassId = normalizeId(item?.classId);
        return !selectedClassId || itemClassId === normalizeId(selectedClassId);
      });
      setSubActivityList(filtered);
    } catch (err) {
      console.error(err);
      setSubActivityList([]);
    }
  };

  const getSuperActivityList = async (selectedClassId, options = {}) => {
    const { autoLoad = true } = options;
    const schoolId = resolveSchoolId(userDetails);
    const classIdValue = normalizeId(selectedClassId);

    if (!schoolId || !classIdValue) {
      setAddSuperActivityList([]);
      setActivityList([]);
      return;
    }

    try {
      getSubActivityMasterList(classIdValue);
      const res = await SuperActivityService.getAllSuperActivityById(schoolId, { classId: classIdValue });
      const areas = Array.isArray(res) ? res : [];
      setAddSuperActivityList(areas);

      if (areas.length > 0) {
        const firstAreaId = areas[0]._id;
        setActiveTab(firstAreaId);
        // Data load is handled by the student/key/tab effect once student is selected.
      } else {
        setActivityList([]);
        setActiveTab("");
      }
    } catch (err) {
      console.error(err);
      setAddSuperActivityList([]);
      setActivityList([]);
    }
  };

  const getSectionListByClass = (selectedClassId) => {
    if (!selectedClassId) {
      setSectionList([]);
      setSectionId("");
      return;
    }
    SectionService.findSectionList({
      schooleId: resolveSchoolId(userDetails),
      classId: selectedClassId,
    })
      .then((res) => {
        setSectionList(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        setSectionList([]);
      });
  };

  const getAddClassList = () => {
    AddClassService.getAllAddClass(resolveSchoolId(userDetails))
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setAddClassList(list);

        if (userDetails.role === "TEACHER" && userDetails.classId) {
          const selectedClassId = normalizeId(userDetails.classId);
          const teacherSectionId = normalizeId(userDetails.sectionId);
          setClassId(selectedClassId);
          setSectionId(teacherSectionId);
          getSectionListByClass(selectedClassId);
          getSuperActivityList(selectedClassId, { autoLoad: false });
          getStudentList(selectedClassId, teacherSectionId || undefined);
        } else if (list.length > 0) {
          const defaultClassId = normalizeId(list[0]._id);
          setClassId(defaultClassId);
          setSectionId("");
          getSectionListByClass(defaultClassId);
          getSuperActivityList(defaultClassId, { autoLoad: false });
          getStudentList(defaultClassId, "");
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getStudentList = (selectedClassId, overrideSectionId) => {
    const effectiveSectionId =
      overrideSectionId !== undefined
        ? overrideSectionId
        : sectionId || userDetails.sectionId || undefined;

    const body = { classId: selectedClassId };
    if (effectiveSectionId) {
      body.sectionId = effectiveSectionId;
    }

    StudentService.getAllStudentById(resolveSchoolId(userDetails), body)
      .then((res) => {
        const studentDetails = (res || []).map((student) => ({
          _id: student._id,
          studentName: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
        }));
        setStudentList(studentDetails);
      })
      .catch((err) => {
        console.error("Error fetching students:", err);
        setStudentList([]);
      });
  };

  const handleClassChange = (selectedClassId) => {
    setClassId(selectedClassId);
    setSectionId("");
    setStudentId("");
    setStudentList([]);
    setActivityList([]);
    getSectionListByClass(selectedClassId);
    getStudentList(selectedClassId, "");
    getSuperActivityList(selectedClassId, { autoLoad: false });
  };

  const handleSectionChange = (newSectionId) => {
    setSectionId(newSectionId);
    setStudentId("");
    setActivityList([]);
    if (classId) {
      getStudentList(classId, newSectionId || undefined);
    }
  };

  const handleTabClick = (superActivityId) => {
    setActiveTab(superActivityId);
  };

  const renderStatusCell = (statusCode, activityRecordsId) => {
    let activityDetails = null;
    if (activityRecordsId && activityRecordsId.length > 0) {
      activityDetails = activityRecordsId[0].statusHistory?.find(
        (item) => item.key === statusCode
      );
    }

    const bgColor = colorMap[statusCode];

    return (
      <td
        className={`${classes.statusCell} ${!activityDetails ? classes.inactiveStatus : ""}`}
        style={{
          backgroundColor: activityDetails ? bgColor : undefined,
          color: activityDetails ? "#ffffff" : undefined,
        }}
      >
        {activityDetails && activityDetails.changedAt
          ? formatDate(activityDetails.changedAt)
          : "-"}
      </td>
    );
  };

  return (
    <Box className={classes.pageWrapper}>
      <Box
        display="flex"
        alignItems="stretch"
        flexWrap="wrap"
        style={{ border: '1px solid #e2e8f0', borderRadius: 24, overflow: 'hidden', background: '#fff' }}
      >
      <Paper
        className={classes.contentCard}
        elevation={0}
        style={{ flex: '1 1 520px', minWidth: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}
      >
        <Box className={classes.headerRow}>
          <Box className={classes.titleBlock}>
            <Typography className={classes.title}>Area Of Work</Typography>
            <Typography className={classes.subtitle}>
              Review a student&apos;s progression through each activity and exercise by status milestone.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon fontSize="small" />}
            onClick={() => setFiltersOpen((o) => !o)}
            aria-pressed={filtersOpen}
            style={filtersOpen ? { backgroundColor: '#e6e6e6' } : undefined}
          >
            Filters
          </Button>
        </Box>

        <Typography className={classes.helperText}>
          Tap an area tab to switch categories. On small screens, swipe horizontally to view the full progress table.
        </Typography>

        <Box className={classes.tabsScroller}>
          <Box className={classes.tabsWrap}>
            {addSuperActivityList.map((area) => {
              const isActive = normalizeId(activeTab) === normalizeId(area._id);
              return (
                <button
                  key={area._id}
                  type="button"
                  onClick={() => handleTabClick(area._id)}
                  className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ""}`}
                >
                  {area.superActivityName}
                </button>
              );
            })}
          </Box>
        </Box>

        {!studentId && !isLoading && (
          <Box className={classes.promptState}>
            Please select a class, section (optional), and student. Results update automatically.
          </Box>
        )}

        {studentId && !isLoading && (() => {
          const groups = (activityList || []).map((activity) => {
            const matchingEntries = (activity.activityList || []).filter((item) => {
              const recordStudentId = item?.studentId?._id || item?.studentId;
              return normalizeId(recordStudentId) === normalizeId(studentId);
            });

            const exerciseMasterList = subActivityList.filter((item) => {
              const subActivityParentId = item?.activityId?._id || item?.activityId;
              return normalizeId(subActivityParentId) === normalizeId(activity.id);
            });

            const recordedExercises = matchingEntries
              .map((entry) => {
                const subActivity = entry?.activityRecordsId?.[0]?.subActivityId;
                if (!subActivity) return null;
                return {
                  id: subActivity?._id || subActivity,
                  name: subActivity?.subActivityName || "-",
                };
              })
              .filter(Boolean);

            const exerciseRows = exerciseMasterList.length > 0
              ? exerciseMasterList.map((item) => ({
                id: item?._id,
                name: item?.subActivityName || "-",
              }))
              : recordedExercises.length > 0
                ? recordedExercises
                : [{ id: null, name: activity.activityName || "-", isActivityLevel: true }];

            const exercises = exerciseRows.map((exercise) => {
              const matchedEntry = matchingEntries.find((entry) => {
                const subActivity = entry?.activityRecordsId?.[0]?.subActivityId;
                const subActivityId = subActivity?._id || subActivity || null;
                if (exercise.id == null) {
                  return !subActivityId;
                }
                return normalizeId(subActivityId) === normalizeId(exercise.id);
              }) || (exercise.id == null ? matchingEntries[0] : null);

              const record = matchedEntry?.activityRecordsId;
              return { ...exercise, record };
            });

            // Exercises with recorded status data first.
            exercises.sort((a, b) => {
              const aHas = hasDisplayableStatus(a.record) ? 1 : 0;
              const bHas = hasDisplayableStatus(b.record) ? 1 : 0;
              return bHas - aHas;
            });

            const performedCount = exercises.filter(
              (exercise) => hasDisplayableStatus(exercise.record)
            ).length;

            return {
              id: activity.id,
              activityName: activity.activityName,
              exercises,
              performedCount,
            };
          })
            // Activities with more performed exercises first.
            .sort((a, b) => b.performedCount - a.performedCount);

          if (groups.length === 0) {
            return (
              <Box className={classes.emptyState}>
                No activity records found for the selected student.
              </Box>
            );
          }

          return (
            <>
              <Box className={classes.groupToolbar}>
                <Button
                  className={classes.toolbarButton}
                  size="small"
                  onClick={() =>
                    setExpandedGroups(
                      groups.reduce((acc, group, idx) => {
                        acc[group.id ?? `group-${idx}`] = true;
                        return acc;
                      }, {})
                    )
                  }
                >
                  Expand all
                </Button>
                <Button
                  className={classes.toolbarButton}
                  size="small"
                  onClick={() => setExpandedGroups({})}
                >
                  Collapse all
                </Button>
              </Box>

              <Box className={classes.groupContainer}>
                {groups.map((group, groupIndex) => {
                  const groupKey = group.id ?? `group-${groupIndex}`;
                  const isOpen = !!expandedGroups[groupKey];
                  return (
                    <Box key={groupKey}>
                      <Box
                        className={`${classes.groupHeader} ${isOpen ? classes.groupHeaderActive : ""}`}
                        onClick={() => toggleGroup(groupKey)}
                      >
                        <Box className={classes.groupHeaderLeft}>
                          {isOpen ? (
                            <KeyboardArrowDownIcon className={classes.groupChevron} />
                          ) : (
                            <KeyboardArrowRightIcon className={classes.groupChevron} />
                          )}
                          <Typography className={classes.groupTitle}>
                            {group.activityName}
                          </Typography>
                        </Box>
                        <Typography className={classes.groupCount}>
                          {group.performedCount} {group.performedCount === 1 ? "exercise performed" : "exercises performed"}
                        </Typography>
                      </Box>

                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box className={classes.tableWrapper} style={{ borderRadius: 0, border: "none" }}>
                          <table className={classes.table}>
                            <thead>
                              <tr>
                                <th className={`${classes.headCell} ${classes.firstHeadCell}`}>Exercise</th>
                                <th className={classes.headCell}>Presented (P)</th>
                                <th className={classes.headCell}>Works with Assistance (WA)</th>
                                <th className={classes.headCell}>Works Independently (WI)</th>
                                <th className={classes.headCell}>Mastered (M)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.exercises.map((exercise, index) => (
                                <tr key={`${groupKey}-${exercise?.id || index}`} className={classes.row}>
                                  <td className={classes.subActivityCell}>
                                    {exercise?.name || "-"}
                                  </td>
                                  {renderStatusCell("P", exercise.record)}
                                  {renderStatusCell("WA", exercise.record)}
                                  {renderStatusCell("WI", exercise.record)}
                                  {renderStatusCell("M", exercise.record)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </>
          );
        })()}

        {isLoading && (
          <Box className={classes.loadingBox}>Loading activity progress...</Box>
        )}
      </Paper>
      {filtersOpen && (
        <FilterPanel onReset={handleResetFilters}>
          <FilterSection title="Class" icon={<SchoolIcon style={{ fontSize: 16 }} />}>
            <FormControl fullWidth variant="outlined" size="small">
              <Select displayEmpty value={classId} onChange={(e) => handleClassChange(e.target.value)}>
                <MenuItem value=""><em>Select Class</em></MenuItem>
                {addClassList.map(({ _id, className }) => (
                  <MenuItem key={_id} value={normalizeId(_id)}>{className}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterSection>
          <FilterSection title="Section" icon={<PersonIcon style={{ fontSize: 16 }} />}>
            <FormControl fullWidth variant="outlined" size="small" disabled={!classId || sectionList.length === 0}>
              <Select displayEmpty value={sectionId} onChange={(e) => handleSectionChange(e.target.value)}>
                <MenuItem value=""><em>All Sections</em></MenuItem>
                {sectionList.map(({ _id, sectionName }) => (
                  <MenuItem key={_id} value={normalizeId(_id)}>{sectionName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterSection>
          <FilterSection title="Student" icon={<PersonIcon style={{ fontSize: 16 }} />}>
            <FormControl fullWidth variant="outlined" size="small">
              <Select displayEmpty value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <MenuItem value=""><em>Select Student</em></MenuItem>
                {studentList.map(({ _id, studentName }) => (
                  <MenuItem key={_id} value={normalizeId(_id)}>{studentName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterSection>
          <FilterSection title="Key" icon={<VpnKeyIcon style={{ fontSize: 16 }} />}>
            <FormControl fullWidth variant="outlined" size="small">
              <Select displayEmpty value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)}>
                <MenuItem value=""><em>All Keys</em></MenuItem>
                {keyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FilterSection>
        </FilterPanel>
      )}
      </Box>
    </Box>
  );
}
